import { createApplication } from '@/gen/catpocs/v1/applications-ApplicationService_connectquery'
import {
  addProjectMember,
  deleteApplication,
  forceDeleteApplication,
  removeProjectMember,
  resumeApplication,
  retryApplicationSetup,
  retryCreateCRD,
  suspendApplication,
  syncApplication,
} from '@/lib/api'
import { invalidateProjectDetailReadModels } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import type { CreateApplicationResponse } from '@/gen/catpocs/v1/applications_pb'

interface UseProjectDetailActionsOptions {
  onCreateApplicationSuccess?: (appId?: string) => void
  onAddMemberSuccess?: () => void
}

export function useProjectDetailActions(options?: UseProjectDetailActionsOptions) {
  const createApplicationMutation = useMutationWithToast<CreateApplicationResponse>(createApplication, {
    successTitle: 'Application created',
    successMessage: 'Navigating to application details...',
    errorTitle: 'Failed to create application',
    invalidate: invalidateProjectDetailReadModels,
    onSuccess: (response) => {
      options?.onCreateApplicationSuccess?.(response.application?.id)
    },
  })

  const deleteApplicationMutation = useMutationWithToast(deleteApplication, {
    successTitle: 'Application deletion started',
    successMessage: 'The application is being deleted.',
    errorTitle: 'Failed to delete application',
    invalidate: invalidateProjectDetailReadModels,
  })

  const retryCreateCRDMutation = useMutationWithToast(retryCreateCRD, {
    successTitle: 'CRD creation retried',
    successMessage: 'The ArgoCD application is being created.',
    errorTitle: 'Failed to retry CRD creation',
    invalidate: invalidateProjectDetailReadModels,
  })

  const retrySetupMutation = useMutationWithToast(retryApplicationSetup, {
    successTitle: 'Setup retry initiated',
    successMessage: 'Repository and AWS setup is being retried.',
    errorTitle: 'Failed to retry setup',
    invalidate: invalidateProjectDetailReadModels,
  })

  const forceDeleteMutation = useMutationWithToast(forceDeleteApplication, {
    successTitle: 'Application force-deleted',
    successMessage: 'The application has been removed.',
    errorTitle: 'Failed to force delete',
    invalidate: invalidateProjectDetailReadModels,
  })

  const syncApplicationMutation = useMutationWithToast(syncApplication, {
    successTitle: 'Sync triggered',
    successMessage: 'Application sync has been initiated.',
    errorTitle: 'Failed to sync application',
    invalidate: invalidateProjectDetailReadModels,
  })

  const suspendApplicationMutation = useMutationWithToast(suspendApplication, {
    successTitle: 'Application disabled',
    successMessage: 'Sync has been suspended and replicas set to zero.',
    errorTitle: 'Failed to disable application',
    invalidate: invalidateProjectDetailReadModels,
  })

  const resumeApplicationMutation = useMutationWithToast(resumeApplication, {
    successTitle: 'Application enabled',
    successMessage: 'Sync has been re-enabled and replicas restored.',
    errorTitle: 'Failed to enable application',
    invalidate: invalidateProjectDetailReadModels,
  })

  const addMemberMutation = useMutationWithToast(addProjectMember, {
    successTitle: 'Member added',
    successMessage: 'The member has been added to the project.',
    errorTitle: 'Failed to add member',
    invalidate: invalidateProjectDetailReadModels,
    onSuccess: () => {
      options?.onAddMemberSuccess?.()
    },
  })

  const removeMemberMutation = useMutationWithToast(removeProjectMember, {
    successTitle: 'Member removed',
    successMessage: 'The member has been removed from the project.',
    errorTitle: 'Failed to remove member',
    invalidate: invalidateProjectDetailReadModels,
  })

  return {
    createApplicationMutation,
    deleteApplicationMutation,
    retryCreateCRDMutation,
    retrySetupMutation,
    forceDeleteMutation,
    syncApplicationMutation,
    suspendApplicationMutation,
    resumeApplicationMutation,
    addMemberMutation,
    removeMemberMutation,
  }
}
